import {
    createContext,
    useContext,
    useState,
    useEffect,
    ReactNode,
    useCallback,
    type Dispatch,
    type SetStateAction,
} from 'react';
import axios from 'axios';
import settings from '../settings';
import {sanitizeCSS} from '../utils/cssSanitizer';

export interface Organization {
    id: number;
    subdomain: string;
    name: string;
    display_name: string;
    support_email?: string;
    support_phone?: string;
    features?: OrganizationFeatures;
}

export interface OrganizationFeatures {
    enable_appointments?: boolean;
    enable_kit_registration?: boolean;
    enable_telehealth?: boolean;
    enable_patient_switcher?: boolean;
    enable_health_profile?: boolean;
}

export interface OrganizationTheme {
    logo_url: string;
    favicon_url: string;
    primary_color: string;
    secondary_color: string;
    accent_color: string;
    font_family: string;
    custom_css?: string;
    welcome_message?: string;
    footer_text?: string;
}

interface TenantContextType {
    organization: Organization | null;
    theme: OrganizationTheme | null;
    loading: boolean;
    error: string | null;
    refetch: () => Promise<void>;
}

const TenantContext = createContext<TenantContextType>({
    organization: null,
    theme: null,
    loading: true,
    error: null,
    refetch: async () => {},
});

/**
 * Get the subdomain override for development/testing
 * Priority: URL parameter > Environment variable > localStorage
 * NOTE: Only works in non-production environments for security
 */
function getSubdomainOverride(): string | null {
    // Only allow subdomain override in development/staging environments
    const environment = import.meta.env.VITE_NODE_ENV || import.meta.env.MODE || 'development';
    const isProduction = environment === 'production';

    if (isProduction) {
        // Clear any existing override in production (security measure)
        localStorage.removeItem('dev_tenant_subdomain');
        return null;
    }

    // Check URL parameter first (e.g., ?subdomain=client1)
    const urlParams = new URLSearchParams(window.location.search);
    const urlSubdomain = urlParams.get('subdomain');
    if (urlSubdomain) {
        console.log(`[Tenant] Using subdomain from URL parameter: ${urlSubdomain}`);
        return urlSubdomain;
    }

    // Check environment variable (e.g., VITE_TENANT_SUBDOMAIN=client1)
    const envSubdomain = import.meta.env.VITE_TENANT_SUBDOMAIN;
    if (envSubdomain) {
        console.log(`[Tenant] Using subdomain from environment variable: ${envSubdomain}`);
        return envSubdomain;
    }

    // Check localStorage for persisted subdomain (for convenience)
    const storedSubdomain = localStorage.getItem('dev_tenant_subdomain');
    if (storedSubdomain) {
        console.log(`[Tenant] Using subdomain from localStorage: ${storedSubdomain}`);
        return storedSubdomain;
    }

    return null;
}

type LoadTenantDataParams = {
    apiURL: string;
    applyTheme: (theme: OrganizationTheme, org: Organization) => void;
    setOrganization: Dispatch<SetStateAction<Organization | null>>;
    setTheme: Dispatch<SetStateAction<OrganizationTheme | null>>;
    setLoading: Dispatch<SetStateAction<boolean>>;
    setError: Dispatch<SetStateAction<string | null>>;
};

async function loadTenantData({
    apiURL,
    applyTheme,
    setOrganization,
    setTheme,
    setLoading,
    setError,
}: LoadTenantDataParams): Promise<void> {
    const subdomainOverride = getSubdomainOverride();

    try {
        setLoading(true);
        setError(null);

        const headers: Record<string, string> = {};
        if (subdomainOverride) {
            headers['X-Tenant-Subdomain'] = subdomainOverride;
        }

        const orgResponse = await axios.get<Organization>(`${apiURL}/api/organization`, {
            headers,
        });
        const org = orgResponse.data;
        setOrganization(org);

        const themeResponse = await axios.get<OrganizationTheme>(`${apiURL}/api/organization/theme`, {
            headers,
        });
        const themeData = themeResponse.data;
        setTheme(themeData);

        applyTheme(themeData, org);

        if (subdomainOverride) {
            localStorage.setItem('dev_tenant_subdomain', subdomainOverride);
        }
    } catch (err: any) {
        console.error('Failed to load tenant data:', err);

        if (subdomainOverride) {
            const storedSubdomain = localStorage.getItem('dev_tenant_subdomain');
            if (storedSubdomain === subdomainOverride) {
                console.warn(`[Tenant] Clearing invalid subdomain from localStorage: ${subdomainOverride}`);
                localStorage.removeItem('dev_tenant_subdomain');
            }
        }

        setError(err.response?.data?.error || 'Failed to load organization');
    } finally {
        setLoading(false);
    }
}

export function TenantProvider({children}: {children: ReactNode}) {
    const [organization, setOrganization] = useState<Organization | null>(null);
    const [theme, setTheme] = useState<OrganizationTheme | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const applyTheme = useCallback((theme: OrganizationTheme, org: Organization) => {
        // Set CSS variables
        const root = document.documentElement;
        root.style.setProperty('--primary-color', theme.primary_color);
        root.style.setProperty('--secondary-color', theme.secondary_color);
        root.style.setProperty('--accent-color', theme.accent_color);
        root.style.setProperty('--font-family', theme.font_family);

        // Set page title
        document.title = org.display_name || org.name;

        // Update favicon
        const favicon = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
        if (favicon && theme.favicon_url) {
            favicon.href = theme.favicon_url;
        } else if (theme.favicon_url) {
            // Create favicon link if it doesn't exist
            const newFavicon = document.createElement('link');
            newFavicon.rel = 'icon';
            newFavicon.href = theme.favicon_url;
            document.head.appendChild(newFavicon);
        }

        // Apply custom CSS if provided (with sanitization to prevent XSS)
        if (theme.custom_css) {
            // Remove existing custom org styles if they exist
            const existingStyle = document.getElementById('custom-org-styles');
            if (existingStyle) {
                existingStyle.remove();
            }

            // Sanitize CSS to prevent XSS attacks and data exfiltration
            const sanitizedCSS = sanitizeCSS(theme.custom_css);

            if (sanitizedCSS) {
                // Add new custom styles (only if sanitization didn't reject everything)
                const style = document.createElement('style');
                style.id = 'custom-org-styles';
                style.textContent = sanitizedCSS;
                document.head.appendChild(style);
            } else {
                console.warn('[Tenant] Custom CSS was rejected by sanitizer');
            }
        }
    }, []);

    const refetch = useCallback(
        () =>
            loadTenantData({
                apiURL: settings.apiURL,
                applyTheme,
                setOrganization,
                setTheme,
                setLoading,
                setError,
            }),
        [applyTheme]
    );

    useEffect(() => {
        void loadTenantData({
            apiURL: settings.apiURL,
            applyTheme,
            setOrganization,
            setTheme,
            setLoading,
            setError,
        });
    }, [applyTheme]);

    return (
        <TenantContext.Provider value={{organization, theme, loading, error, refetch}}>
            {children}
        </TenantContext.Provider>
    );
}

export function useTenant() {
    const context = useContext(TenantContext);
    if (!context) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
}
